import { useEffect, useRef, useState } from "react";
import { ArrowRight, ImagePlus, Upload, X } from "lucide-react";
import { api, isUrl, mediaUrl, uploadMedia } from "@/lib/api";
import { ADMIN_RESOURCES, FIELD_OPTIONS, USER_STATUSES } from "./resources";
import { RichTextEditor } from "@/components/RichText";

const LONG_FIELDS = new Set(["description", "bio", "excerpt", "message"]);
const RICH_FIELDS = new Set(["content"]);
const MEDIA_FIELDS = new Set(["logo", "image", "banner", "cover", "thumbnail", "file_url"]);

export const MediaField = ({ k, label, value, onChange }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    try { onChange((await uploadMedia(f)).url); }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const image = isUrl(value) && k !== "file_url";
  return (
    <label className="media-field">
      {label}
      <div className="media-field-row">
        {image && <img src={mediaUrl(value)} alt="" data-testid={`admin-preview-${k}`} />}
        <input value={value || ""} onChange={e => onChange(e.target.value)} placeholder="Paste a URL or upload from device" data-testid={`admin-input-${k}`} />
        <button type="button" className="button button-ghost dark" onClick={() => ref.current?.click()} disabled={busy} data-testid={`admin-upload-${k}`}>
          <ImagePlus size={14} /> {busy ? "Uploading…" : "Upload"}
        </button>
        <input ref={ref} type="file" hidden accept={k === "file_url" ? "application/pdf,image/*" : "image/*"} onChange={pick} data-testid={`admin-file-${k}`} />
      </div>
      {err && <small className="field-error" data-testid={`admin-upload-error-${k}`}>{err}</small>}
    </label>
  );
};

export const AdminEditor = ({ resource, record, onClose, onSaved }) => {
  const meta = ADMIN_RESOURCES.find(r => r.key === resource);
  const [form, setForm] = useState(record || {});
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(record || {}), [record]);

  const save = async e => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const path = record?.id ? `/admin/resources/${resource}/${record.id}` : `/admin/resources/${resource}`;
      await api(path, { method: record?.id ? "PUT" : "POST", body: JSON.stringify(form) });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="admin-modal" role="dialog" data-testid="admin-editor-modal">
      <form className="admin-modal-body" onSubmit={save}>
        <div className="admin-modal-head">
          <div>
            <span className="eyebrow">{record?.id ? "Edit" : "Create"}</span>
            <h2>{meta?.label || resource}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="admin-editor-close" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="admin-modal-fields">
          {(meta?.fields || []).map(([k, label]) => (
            MEDIA_FIELDS.has(k) ? (
              <MediaField key={k} k={k} label={label} value={form[k]} onChange={v => setForm({ ...form, [k]: v })} />
            ) : RICH_FIELDS.has(k) ? (
              <div key={k} className="rich-label"><span>{label}</span><RichTextEditor value={form[k]} onChange={v => setForm(f => ({ ...f, [k]: v }))} testId={`admin-rich-${k}`} /></div>
            ) : (
            <label key={k}>
              {label}
              {(k === "role" || (k === "status" && resource === "users")) ? (
                <select value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-input-${k}`}>
                  <option value="">Select…</option>
                  {(k === "role" ? FIELD_OPTIONS.role : USER_STATUSES).map(o => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
                </select>
              ) : k === "password" ? (
                record?.id ? <small className="hint">Use "Reset password" from the list to change an existing user's password.</small> : <input type="password" minLength={8} value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid="admin-input-password" />
              ) : LONG_FIELDS.has(k) ? (
                <textarea rows={3} value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-input-${k}`} />
              ) : (
                <input value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-input-${k}`} />
              )}
            </label>
            )
          ))}
        </div>
        {err && <div className="error-message">{err}</div>}
        <div className="admin-modal-actions">
          <button type="button" className="button button-ghost dark" onClick={onClose} data-testid="admin-editor-cancel">Cancel</button>
          <button className="button button-gold" disabled={saving} data-testid="admin-editor-save">
            {saving ? "Saving…" : "Save record"} <ArrowRight size={15} />
          </button>
        </div>
      </form>
    </div>
  );
};

export const AdminUpload = ({ onClose, onUploaded }) => {
  const fileRef = useRef();
  const [status, setStatus] = useState("");
  const [uploading, setUploading] = useState(false);

  const upload = async e => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return setStatus("Choose a file first.");
    setUploading(true); setStatus("");
    try {
      const data = await uploadMedia(f);
      setStatus(`Uploaded: ${data.name}`);
      onUploaded(data);
    } catch (e) { setStatus(e.message); }
    finally { setUploading(false); }
  };

  return (
    <div className="admin-modal" role="dialog" data-testid="admin-upload-modal">
      <form className="admin-modal-body" onSubmit={upload}>
        <div className="admin-modal-head">
          <div><span className="eyebrow">Media</span><h2>Upload a file</h2></div>
          <button type="button" onClick={onClose} data-testid="admin-upload-close" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="admin-modal-fields">
          <label style={{ gridColumn: "1/-1" }}>Choose a file (JPG, PNG, WEBP, PDF or MP4, up to 10 MB)
            <input ref={fileRef} type="file" accept="image/*,application/pdf,video/mp4" data-testid="admin-upload-input" />
          </label>
          {status && <div className="hint" data-testid="admin-upload-status">{status}</div>}
        </div>
        <div className="admin-modal-actions">
          <button type="button" className="button button-ghost dark" onClick={onClose} data-testid="admin-upload-cancel">Close</button>
          <button className="button button-gold" disabled={uploading} data-testid="admin-upload-submit">
            {uploading ? "Uploading…" : "Upload"} <Upload size={15} />
          </button>
        </div>
      </form>
    </div>
  );
};
